// Описание игры: контракт эндпоинта /api/game-config.
// Структуры повторяют записи бэкенда в src/Backend/Game/GameConfig.cs.

import { getJson } from './http';

const GAME_CONFIG_URL = '/api/game-config';

/** Идентификатор символа. Совпадает с идентификаторами из GDD: id_00 … id_11. */
export type SymbolId = string;

/** Роль символа в игре. */
export type SymbolRole = 'regular' | 'wild' | 'scatter';

/** Символ барабана. Назван GameSymbol, чтобы не перекрывать глобальный Symbol. */
export interface GameSymbol {
  readonly id: SymbolId;
  readonly role: SymbolRole;
}

/** Полоса символов одного барабана. Порядок задаёт полосу. */
export type Reel = readonly SymbolId[];

/**
 * Линия выплат: индекс ряда на каждом барабане слева направо. Ряд 0 — верхний, ряд 2 — нижний.
 * Например, [0, 0, 0] — верхняя линия, [1, 1, 1] — центральная, [0, 1, 2] — диагональ.
 */
export type Payline = readonly number[];

/**
 * Сумма денег в центах. Всегда целое число: номинал по GDD равен 0.01, дробных денег в игре нет.
 * Для показа игроку дели на 100 только при форматировании, счёт веди в центах.
 */
export type Cents = number;

/** Выплаты одного символа: количество символов в комбинации -> выплата в центах. */
export type SymbolPayouts = Readonly<Record<number, Cents>>;

/**
 * Таблица выплат: идентификатор символа -> выплаты этого символа.
 * Суммы заданы для базовой ставки 1 цент на линию, то есть 9 центов за спин.
 */
export type Paytable = Readonly<Record<SymbolId, SymbolPayouts>>;

/** Как выпавший множитель двигает манометр. */
export type GaugeCharge = 'add' | 'copy' | 'raise';

/** Как несколько множителей на одной линии складываются в один. */
export type MultiplierStacking = 'sum' | 'product' | 'highest';

/** Одно значение множителя и то, насколько часто оно выпадает. */
export interface MultiplierWeight {
  readonly value: number;
  readonly weight: number;
}

/**
 * Описание фичи Steam Spin, см. GDD, раздел 5.
 *
 * Доску мини-слота фича зовёт по имени: ту же доску крутит Red Hot Spin, см. GDD, раздел 6.2.
 */
export interface SteamSpinConfig {
  /** Символ wild стадии 1. Он открывает бонусные барабаны. */
  readonly wildSymbol: SymbolId;

  /** Символ wild multiplier стадии 2. Он подменяет собой wild в полосах. */
  readonly multiplierSymbol: SymbolId;

  /** Значения множителя и их веса. Значение выбирает бэкенд, клиент его только показывает. */
  readonly multipliers: readonly MultiplierWeight[];

  /** Наибольший заряд манометра. По GDD, раздел 5.2, это 10. */
  readonly gaugeMax: number;

  /** Заряд, который манометр получает вместе с открытием своего барабана. */
  readonly openGauge: number;

  readonly charge: GaugeCharge;
  readonly stacking: MultiplierStacking;

  /** Имя доски мини-слота 1x3 в описании игры. */
  readonly bonusBoard: SymbolId;
}

/**
 * Описание фичи Red Hot Spin, см. GDD, раздел 6.
 *
 * Скаттеры на экране перегревают манометры фичи Steam Spin: все они разом встают на потолок,
 * и мини-слот крутится с общим множителем в три десятки, см. GDD, раздел 6.2.
 *
 * Решений клиент по этому описанию не принимает: скаттеры считает бэкенд и присылает готовый
 * бонус вместе с шагами манометров.
 */
export interface RedHotSpinConfig {
  /** Сколько скаттеров на экране запускают бонус. По GDD, раздел 6.1, это 3. */
  readonly scatters: number;
}

/**
 * Описание доски игры: символы, полосы барабанов, высота поля, линии и таблица выплат.
 *
 * Доска — это барабаны, окна, линии и выплаты вместе. У мини-слота 1x3 своя единственная
 * линия и своя таблица, поэтому он описан этой же записью и лежит в boards.
 *
 * Надстройки висят необязательными полями. Слот без надстройки их не присылает вовсе.
 */
export interface GameConfig {
  readonly symbols: readonly GameSymbol[];
  readonly reels: readonly Reel[];
  /** Число видимых рядов поля, одно на все барабаны. */
  readonly rows: number;
  readonly paylines: readonly Payline[];
  readonly paytable: Paytable;

  /** Дополнительные доски игры по именам. Нет поля — доска у игры одна. */
  readonly boards?: Readonly<Record<string, GameConfig>>;

  /** Описание фичи Steam Spin. Нет поля — в этом слоте фичи нет. */
  readonly steamSpin?: SteamSpinConfig;

  /** Описание фичи Red Hot Spin. Нет поля — в этом слоте фичи нет. */
  readonly redHotSpin?: RedHotSpinConfig;

  /**
   * Кратность общей ставки, с которой игра показывает Big Win, см. GDD, раздел 3.7.
   *
   * Порог считается от ставки игрока: сумма верна только на одной ставке, см. ADR-24.
   * Нет поля — крупные выигрыши не выделяются, и сумма всегда всплывает в середине поля.
   */
  readonly bigWinRate?: number;

  /**
   * Ставки за спин, из которых игрок выбирает, см. ADR-33.
   * Нет поля — выбирать не из чего, и раунд идёт базовой ставкой.
   */
  readonly stakes?: readonly Cents[];
}

/**
 * Дополнительная доска игры по имени.
 *
 * @throws Error если доски с таким именем в описании игры нет
 */
export function boardOf(config: GameConfig, name: string): GameConfig {
  const board: GameConfig | undefined = config.boards?.[name];
  if (board === undefined)
    throw new Error(`В описании игры нет доски "${name}"`);

  return board;
}

/**
 * Базовая ставка доски в центах: 1 цент на линию, см. ADR-13.
 *
 * На неё задана таблица выплат, которую присылает бэкенд, и от неё считается ставка игрока.
 */
export function baseStake(config: GameConfig): Cents {
  return config.paylines.length;
}

/**
 * Переводит выплату с базовой ставки на ставку игрока, округляя до цента.
 *
 * Счёт тот же, что у бэкенда, см. GameConfig.Scale в src/Backend/Game/GameConfig.cs. Игра
 * его повторяет только ради таблицы выплат: сами выигрыши приходят уже пересчитанными.
 * Разойдись эти два счёта — таблица обещала бы игроку не ту сумму, которую он получит.
 */
export function scalePayout(payout: Cents, stake: Cents, base: Cents): Cents {
  return Math.floor((payout * stake + Math.floor(base / 2)) / base);
}

/** Грузит описание игры с бэкенда. */
export function fetchGameConfig(): Promise<GameConfig> {
  return getJson<GameConfig>(GAME_CONFIG_URL);
}
